import { BlockingItem, ResourceRequirement, Shift, VisitOptimizerPreferences } from "@caredna-marlin/visit-optimizer/types";
import { VisitWarnings } from "./visit-warnings";
import { first, last, partition } from "lodash";

export const FAKE_SHIFT_ID = -1
const DURATION_WARNING_THRESHOLD = 5
const MIN_GAP_WARNING_THRESHOLD = 15
const MAX_GAP_WARNING_THRESHOLD = 90
const DRIVE_TIME_WARNING_THRESHOLD = 120
const OVERLAP_WARNING_THRESHOLD = 5
export const LARGE_OVERLAP_THRESHOLD = 15
export const WARNING_SEVERITIES = {
  NONE: 0,

  GAP: 1,
  DURATION: 1,

  TEAM: 2,
  SMALL_OVERLAP: 2,
  CLINICAL_CONSTRAINT: 2,

  LARGE_OVERLAP: 3,
  STATE_LICENSE: 3,
  SERVICE_LINE: 3,
  DRIVE: 3,
  SHIFT: 3,
  REQUIREMENTS: 3,
  INACTIVE_PROVIDER: 3,
  UNASSIGNED: 3,
} as const

export const WARNING_CATEGORY_LABELS: Record<WarningCategory, string> = {
  NONE: "None",
  GAP: "Scheduling Gap",
  DURATION: "Duration Mismatch",
  SHIFT: "Shift Missing",
  TEAM: "Team Mismatch",
  CLINICAL_CONSTRAINT: "Clinical Constraint",
  DRIVE: "Excessive Drive Time",
  STATE_LICENSE: "Provider Not Licensed",
  SERVICE_LINE: "Service Line Mismatch",
  SMALL_OVERLAP: "Minor Overlap",
  LARGE_OVERLAP: "Major Overlap",
  REQUIREMENTS: "Requirement Missing",
  INACTIVE_PROVIDER: "Provider Not Active",
  UNASSIGNED: "No provider assigned",
};

export type WarningCategory = keyof typeof WARNING_SEVERITIES;

export const getOverlapCategory = (minutes: number): WarningCategory => {
  if (minutes <= OVERLAP_WARNING_THRESHOLD) return "NONE";
  return minutes <= LARGE_OVERLAP_THRESHOLD ? "SMALL_OVERLAP" : "LARGE_OVERLAP";
}

export type WarningSeverityValue = typeof WARNING_SEVERITIES[keyof typeof WARNING_SEVERITIES];

export type VisitResourceToCheck = {
  id: number;
  visitId: number;
  providerId: number;
  startTime: Date;
  endTime: Date;
  interVisitDriveTime: number;
  nextVisitDriveTime?: number; // used for "override visit" flow
  startOfShiftDriveTime: number;
  endOfShiftDriveTime: number;
}

export type VisitWarningShift = Pick<Shift, 'shiftId' | 'startTime' | 'endTime' | 'coordinates' | 'startLocation' | 'endLocation' | 'status'>;
export type VisitWarningPreferences = Pick<VisitOptimizerPreferences, 'ignoreDriveTimeShiftStart' | 'ignoreDriveTimeShiftEnd' | 'minutesOfBufferTime' | 'maxGracePeriod' | 'trafficBreakpoints'>
export type VisitWarningResourceRequirement = Pick<ResourceRequirement, 'duration' | 'inHome'> & {
  id: number;
  providerTypeIds: number[]
}

export type VisitResourceWarnings = {
  visitId: number;
  providerId: number;
  severity: WarningSeverityValue;
  shiftMissing?: boolean;
  duration?: { expectedDurationInMinutes: number, actualDurationInMinutes: number }
  previousVisitDriveTimeOverlap?: number;
  nextVisitDriveTimeOverlap?: number;
  startOfShiftDriveTimeOverlap?: number;
  endOfShiftDriveTimeOverlap?: number;
  gapCreatedBeforeVisit?: number;
  gapCreatedAfterVisit?: number;
  excessiveDriveTimeBeforeVisit?: number;
  excessiveDriveTimeAfterVisit?: number;
  previousBlocker?: BlockingItem;
  nextBlocker?: BlockingItem;
  providerNotLicensed?: string; // State where provider is not licensed
  providerServiceLineMismatch?: string; // Service line name when provider is not assigned to it
  providerNotActive?: boolean;
}

export type VisitWarningProviderInfo = {
  active?: boolean;
  providerTypeId: number | null;
  providerType?: { requireStateLicense?: boolean } | null;
  traits?: Array<{ trait: string; value: string }> | null;
  allowedServiceLineIds?: number[] | null;
}
type Boundary = 'startOfShift' | 'previousVisit' | 'nextVisit' | 'endOfShift' // Should be prefixes of overlap types in VisitResourceWarnings
type OverlapKey = `${Boundary}DriveTimeOverlap`;

const overlapKey: Record<Boundary, OverlapKey> = {
  startOfShift: "startOfShiftDriveTimeOverlap",
  previousVisit: "previousVisitDriveTimeOverlap",
  nextVisit: "nextVisitDriveTimeOverlap",
  endOfShift: "endOfShiftDriveTimeOverlap",
};

export const getVisitResourceWarnings = (visitResource: VisitResourceToCheck, resourceRequirement: VisitWarningResourceRequirement, shift: VisitWarningShift, blockers: BlockingItem[], preferences: VisitWarningPreferences, patientState?: string | null, providerInfo?: VisitWarningProviderInfo, serviceLine?: { id: number; name: string } | null): VisitResourceWarnings => {

  const neighbors = getVisitResourceNeighbors(visitResource.startTime, visitResource.endTime, shift, blockers)
  const { previousTimeBoundary, nextTimeBoundary, availableDriveTimeBefore, availableDriveTimeAfter, startsShift, endsShift, previousBlocker, nextBlocker } = neighbors;

  const warnings: VisitResourceWarnings = {
    visitId: visitResource.visitId,
    providerId: visitResource.providerId,
    severity: WARNING_SEVERITIES.NONE,
    previousBlocker,
    nextBlocker,
  }

  if (patientState && providerInfo) {
    const requiresLicense = providerInfo.providerType?.requireStateLicense === true;
    
    if (requiresLicense) {
      const hasLicense = providerInfo.traits?.some(trait => {
        const isStateLicense = trait.trait === 'stateLicense';
        const matchesState = trait.value === patientState;
        return isStateLicense && matchesState;
      }) || false;

      if (!hasLicense) {
        warnings.providerNotLicensed = patientState;
        setWarningSeverity(warnings, WARNING_SEVERITIES.STATE_LICENSE);
      }
    }
  }

  // Check service line assignment
  if (providerInfo && serviceLine) {
    const hasServiceLine = providerInfo.allowedServiceLineIds?.includes(serviceLine.id) ?? false;

    if (!hasServiceLine) {
      warnings.providerServiceLineMismatch = serviceLine.name;
      setWarningSeverity(warnings, WARNING_SEVERITIES.SERVICE_LINE);
    }
  }

  if (shift.shiftId == FAKE_SHIFT_ID || shift.status === 'canceled') {
    setWarningSeverity(warnings, WARNING_SEVERITIES.SHIFT)
    warnings.shiftMissing = true;
  }

  if (providerInfo?.active === false) {
    warnings.providerNotActive = true;
    setWarningSeverity(warnings, WARNING_SEVERITIES.INACTIVE_PROVIDER);
  }

  if (resourceRequirement) {
    const expectedDurationInMinutes = resourceRequirement.duration;
    const actualDurationInMinutes =  Math.floor((visitResource.endTime.getTime() - visitResource.startTime.getTime()) / 1000 / 60);
    const durationDifferenceInMinutes = resourceRequirement.duration - actualDurationInMinutes
    if (durationDifferenceInMinutes > DURATION_WARNING_THRESHOLD) {
      setWarningSeverity(warnings, WARNING_SEVERITIES.DURATION)
      warnings.duration = { expectedDurationInMinutes, actualDurationInMinutes }
    }
  }

  const intersectionBefore = (previousTimeBoundary.getTime() - visitResource.startTime.getTime()) / 60000;
  const intersectionAfter = (visitResource.endTime.getTime() - nextTimeBoundary.getTime()) / 60000;

  const driveTimeToVisit = startsShift ? visitResource.startOfShiftDriveTime : visitResource.interVisitDriveTime
  const driveTimeFromVisit = endsShift ? visitResource.endOfShiftDriveTime : visitResource.nextVisitDriveTime || 0
  
  // Default inHome to true if unknown to use more stringent warnings
  const inHome = resourceRequirement?.inHome == undefined ? true : resourceRequirement.inHome

  const previousBoundary: Boundary = startsShift ? 'startOfShift' : 'previousVisit'
  const nextBoundary: Boundary = endsShift ? 'endOfShift' : 'nextVisit'
  overlapAndGapWarnings(intersectionBefore, availableDriveTimeBefore, driveTimeToVisit, previousBoundary, preferences, inHome, warnings)
  overlapAndGapWarnings(intersectionAfter, availableDriveTimeAfter, driveTimeFromVisit, nextBoundary, preferences, inHome, warnings)

  return warnings
}

export const overlapAndGapWarnings = (
  intersectionMinutes: number,
  availableDriveTime: number,
  driveTime: number,
  boundary: Boundary,
  preferences: VisitWarningPreferences,
  inHome: boolean,
  warnings: VisitResourceWarnings,
) => {
  if (driveTime > DRIVE_TIME_WARNING_THRESHOLD) {
    setWarningSeverity(warnings, WARNING_SEVERITIES.DRIVE)
    if (isEndBoundary(boundary)) {
      warnings.excessiveDriveTimeAfterVisit = driveTime
    } else {
      warnings.excessiveDriveTimeBeforeVisit = driveTime
    }
  }

  // We don't want to generate any warnings that refer to the dummy shift other than drive time.
  if (warnings.shiftMissing && isShiftBookend(boundary)) {
    return;
  }

  const bufferTime = inHome ? preferences.minutesOfBufferTime : 0
  const ignoreDriveForBookend = isEndBoundary(boundary) ? preferences.ignoreDriveTimeShiftEnd : preferences.ignoreDriveTimeShiftStart
  if (!isShiftBookend(boundary)) driveTime += bufferTime
  if (isShiftBookend(boundary) && ignoreDriveForBookend) driveTime = 0
  const gap = Math.round((availableDriveTime - driveTime) + preferences.maxGracePeriod)
  // negative gap => how many minutes the drive time overlaps
  const driveTimeOverlap = 0 - gap;
  // With locationless blocks, there might be no drive time overlap, but the actual visit durations overlap
  const intersectionOverlap = Math.round(intersectionMinutes - preferences.maxGracePeriod);

  const finalOverlap = Math.max(driveTimeOverlap, intersectionOverlap)
  const overlapSeverity = WARNING_SEVERITIES[getOverlapCategory(finalOverlap)];

  if (overlapSeverity) {
    setWarningSeverity(warnings, overlapSeverity)
    warnings[overlapKey[boundary]] = finalOverlap;
  }

  if (gap > MIN_GAP_WARNING_THRESHOLD && gap < MAX_GAP_WARNING_THRESHOLD) {
    setWarningSeverity(warnings, WARNING_SEVERITIES.GAP)
    const gapProp = isEndBoundary(boundary) ? 'gapCreatedAfterVisit' : 'gapCreatedBeforeVisit';
    warnings[gapProp] = gap;
  }
}

/**
 * Holds info about the visits immediately before/after a given start time.
 */
type VisitResourceNeighbors = {
  previousLocation: string;
  nextLocation: string;
  previousTimeBoundary: Date;
  nextTimeBoundary: Date;
  availableDriveTimeBefore: number;
  availableDriveTimeAfter: number;
  startsShift: boolean;
  endsShift: boolean;
  previousBlocker?: BlockingItem,
  nextBlocker?: BlockingItem,
}

export const setWarningSeverity = (warning: VisitResourceWarnings | VisitWarnings, severity: WarningSeverityValue) => {
  warning.severity = Math.max(warning.severity, severity) as WarningSeverityValue
}

const isShiftBookend = (boundary: Boundary): boolean => {
  return boundary == 'startOfShift' || boundary == 'endOfShift'
}

const isEndBoundary = (boundary: Boundary): boolean => {
  return boundary == 'endOfShift' || boundary == 'nextVisit'
}

const minutesBetweenNoNegative = (a: Date, b: Date): number =>
  Math.max(0, (b.getTime() - a.getTime()) / 60000);

export const getVisitResourceNeighbors = (
  startTime: Date,
  endTime: Date,
  shift: VisitWarningShift,
  blockers: BlockingItem[]
): VisitResourceNeighbors => {
  // Sort by start for deterministic scanning
  blockers = [...blockers].sort((a, b) => a.startTime.getTime() - b.startTime.getTime());

  // Partition relative to the visit start
  const [earlier, later] = partition(blockers, b => b.startTime <= startTime);

  // Immediate neighbors by time
  const previous = last(earlier) ?? null;
  const next = first(later) ?? null;

  // Time boundaries are closest blockers whether locationless or not
  const previousTimeBoundary = previous ? previous.endTime : shift.startTime;
  const nextTimeBoundary = next ? next.startTime : shift.endTime;

  // Find previousLocation by "looking past" locationless blockers backward
  // Scan backward from `previous` until we find a blocker with coords; fall back to shift start.
  let previousLocation: string = shift.startLocation;
  let availableDriveTimeBefore: number = 0;
  let driveTimePointer: Date = startTime;
  let addShiftDrive: boolean = true;

  for (let i = earlier.length - 1; i >= 0; i--) {
    const b = earlier[i];
    availableDriveTimeBefore += minutesBetweenNoNegative(b.endTime, driveTimePointer);
    driveTimePointer = b.startTime;
    if (b.coordinates) {
      previousLocation = b.coordinates;
      addShiftDrive = false;
      break;
    }
  }
  if (addShiftDrive) availableDriveTimeBefore += minutesBetweenNoNegative(shift.startTime, driveTimePointer);
  // if none had coordinates, keep shift.startLocation

  // Find nextLocation by "looking past" locationless blockers forward
  let nextLocation: string = shift.endLocation;
  let availableDriveTimeAfter: number = 0;

  driveTimePointer = endTime;
  addShiftDrive = true;
  for (let i = 0; i < later.length; i++) {
    const b = later[i];
    availableDriveTimeAfter += minutesBetweenNoNegative(driveTimePointer, b.startTime);
    driveTimePointer = b.endTime;
    if (b.coordinates) {
      nextLocation = b.coordinates;
      addShiftDrive = false;
      break;
    }
  }
  if (addShiftDrive) {
    availableDriveTimeAfter += minutesBetweenNoNegative(driveTimePointer, shift.endTime);
  }
  // if none had coordinates, keep shift.endLocation

  return {
    previousLocation,
    nextLocation,
    previousTimeBoundary,
    nextTimeBoundary,
    availableDriveTimeBefore,
    availableDriveTimeAfter,
    startsShift: previous == null,
    endsShift: next == null,
    previousBlocker: previous ?? undefined,
    nextBlocker: next ?? undefined,
  };
};
